// src/AuditLogs.js
import React, { useState, useEffect, useContext } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AuthContext } from './context/AuthContext';
import axiosInstance from './axiosInstance';
import { initiateSocket, getSocket } from './socket';
import CompanySelector from './components/CompanySelector';
import DoorFunctions from './DoorFunctions';
import './styles/AuditLogs.css';

// Allowed emails for Door Functions tab
const DOOR_FUNCTIONS_EMAILS = [
  'ericolsen@solu-m.com',
  'ericolsen@solum-group.com'
];

// Company Access Selector Component
const CompanyAccessSelector = ({ userId, currentCompanyAccess, onChange, onDenyRequest }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selected, setSelected] = useState(currentCompanyAccess.companyAccess || []);
  const [allCompanies, setAllCompanies] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen && allCompanies.length === 0) {
      fetchCompanies();
    }
  }, [isOpen]);

  const fetchCompanies = async () => {
    setLoading(true);
    try {
      const { data } = await axiosInstance.get('/function-assignment/companies');
      const companies = (data.companyList || [])
        .filter(c => c.code && c.name)
        .map(c => ({ code: c.code, name: c.name }))
        .sort((a, b) => a.code.localeCompare(b.code));
      setAllCompanies(companies);
    } catch (err) {
      console.error('Error fetching companies:', err);
    } finally {
      setLoading(false);
    }
  };

  const toggleCompany = (code) => {
    setSelected(prev =>
      prev.includes(code)
        ? prev.filter(c => c !== code)
        : [...prev, code]
    );
  };

  // Special code for "All Companies" access
  const ALL_COMPANIES_CODE = '*';

  const toggleAllCompaniesAccess = () => {
    if (selected.includes(ALL_COMPANIES_CODE)) {
      // Remove all access
      setSelected([]);
    } else {
      // Grant all access - just use the special code
      setSelected([ALL_COMPANIES_CODE]);
    }
  };

  const hasAllAccess = selected.includes(ALL_COMPANIES_CODE);

  const handleSave = () => {
    onChange(userId, selected);
    setIsOpen(false);
  };

  const handleCancel = () => {
    setSelected(currentCompanyAccess.companyAccess || []);
    setIsOpen(false);
  };

  const pendingRequests = currentCompanyAccess.pendingCompanyRequests || [];

  const getButtonLabel = () => {
    if (hasAllAccess) return 'All Companies';
    if (selected.length > 0) return `${selected.length} companies`;
    return 'Assign Companies';
  };

  return (
    <div className="company-access-selector">
      {!isOpen ? (
        <div>
          <button
            onClick={() => setIsOpen(true)}
            className={`btn-edit-functions ${hasAllAccess ? 'all-access' : ''}`}
          >
            {getButtonLabel()}
          </button>
          {pendingRequests.length > 0 && (
            <span className="pending-badge" title={`${pendingRequests.length} pending request(s)`}>
              {pendingRequests.length}
            </span>
          )}
        </div>
      ) : (
        <div className="company-access-selector-modal">
          {pendingRequests.length > 0 && (
            <div className="pending-requests-section">
              <h4>Pending Requests ({pendingRequests.length})</h4>
              {pendingRequests.map(req => (
                <div key={req.companyCode} className="pending-request-item">
                  <span><strong>{req.companyCode}</strong> - {req.companyName}</span>
                  <span className="request-date">
                    {new Date(req.requestedAt).toLocaleDateString()}
                  </span>
                  <div className="request-actions">
                    <button
                      onClick={() => {
                        toggleCompany(req.companyCode);
                      }}
                      className="btn-approve"
                      title="Grant access"
                    >
                      Approve
                    </button>
                    <button
                      onClick={() => onDenyRequest(userId, req.companyCode)}
                      className="btn-deny"
                      title="Deny request"
                    >
                      Deny
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="company-list-section">
            <h4>Company Access</h4>
            {loading ? (
              <p>Loading companies...</p>
            ) : (
              <>
                <label className={`all-access-checkbox ${hasAllAccess ? 'active' : ''}`}>
                  <input
                    type="checkbox"
                    checked={hasAllAccess}
                    onChange={toggleAllCompaniesAccess}
                  />
                  <span>Grant Access to ALL Companies</span>
                </label>

                {!hasAllAccess && (
                  <>
                    <p className="or-divider">— or select specific companies —</p>
                    <div className="company-checkboxes">
                      {allCompanies.map(company => (
                        <label key={company.code} className="company-checkbox">
                          <input
                            type="checkbox"
                            checked={selected.includes(company.code)}
                            onChange={() => toggleCompany(company.code)}
                          />
                          <span>{company.code} - {company.name}</span>
                        </label>
                      ))}
                    </div>
                  </>
                )}
              </>
            )}
          </div>

          <div className="company-access-selector-actions">
            <button onClick={handleSave} className="btn-save">Save</button>
            <button onClick={handleCancel} className="btn-cancel">Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
};

/* GA_* live behind the single Global Action Center page, so they get their own
 * tab rather than nine more rows in the SaaS column. */
const GAC_GROUP = 'Global Action Center';

// Function Selector Component (fetches definitions from API)
/* `scope` picks the catalogue: 'internal' for employees, 'portal' for customer
 * accounts. They must not mix — an internal tool can never be ticked for an
 * outside account, and PORTAL_* codes are meaningless on an employee. Customers
 * also cannot hold authorizedForAll (the User model refuses it), so the
 * "Authorize All" toggle is hidden for them rather than shown and rejected. */
const FunctionSelector = ({ userId, currentFunctions, authorizedForAll: initialAuthorizedForAll, onChange, scope = 'internal' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selected, setSelected] = useState(currentFunctions);
  const [authorizedForAll, setAuthorizedForAll] = useState(initialAuthorizedForAll || false);
  const [allFunctions, setAllFunctions] = useState({});
  const [activeGroup, setActiveGroup] = useState(null);
  const [loadingFns, setLoadingFns] = useState(false);

  useEffect(() => {
    if (isOpen && Object.keys(allFunctions).length === 0) {
      setLoadingFns(true);
      axiosInstance.get('/function-definitions')
        .then(({ data }) => {
          const grouped = {};
          (data.functions || [])
            // Definitions predating the scope field are internal by default.
            .filter(fn => fn.enabled && (fn.scope || 'internal') === scope)
            .filter(fn => scope === 'portal' || fn.category !== 'Admin')
            .forEach(fn => {
              const cat = scope === 'portal'
                ? 'Customer'
                // GA_* are the tools INSIDE Global Action Center, not SaaS apps
                // in their own right. Left in the SaaS bucket they made that one
                // column nine rows longer than the page they belong to, which is
                // what made this modal unreadable.
                : (/^GA_/.test(fn.code) ? GAC_GROUP
                  : (fn.category === 'On-Prem' ? 'On-Prem' : 'SaaS'));
              if (!grouped[cat]) grouped[cat] = [];
              grouped[cat].push({ code: fn.code, name: fn.name });
            });
          setAllFunctions(grouped);
          setActiveGroup(prev => (prev && grouped[prev]) ? prev : (Object.keys(grouped)[0] || null));
        })
        .catch(err => console.error('Failed to load function definitions:', err))
        .finally(() => setLoadingFns(false));
    }
  }, [isOpen]);

  const groupNames = Object.keys(allFunctions);
  const activeCodes = (allFunctions[activeGroup] || []).map(f => f.code);
  const activeChosen = activeCodes.filter(c => selected.includes(c)).length;
  const activeAllSelected = activeCodes.length > 0 && activeChosen === activeCodes.length;

  /* Select-all scoped to the visible tab. Granting every Global Action Center
   * tool took ten clicks; it is now one, without also granting unrelated apps
   * the way "Authorize All Functions" does. */
  const toggleGroup = (codes, allSelected) => {
    setSelected(prev => allSelected
      ? prev.filter(c => !codes.includes(c))
      : [...new Set([...prev, ...codes])]);
  };

  const getAllCodes = () => Object.values(allFunctions).flat().map(fn => fn.code);

  const toggleFunction = (code) => {
    setSelected(prev =>
      prev.includes(code)
        ? prev.filter(c => c !== code)
        : [...prev, code]
    );
  };

  const toggleAll = () => {
    if (authorizedForAll) {
      setAuthorizedForAll(false);
      setSelected([]);
    } else {
      setAuthorizedForAll(true);
      setSelected(getAllCodes());
    }
  };

  const handleSave = () => {
    onChange(userId, selected, authorizedForAll);
    setIsOpen(false);
  };

  const handleCancel = () => {
    setSelected(currentFunctions);
    setIsOpen(false);
  };

  return (
    <div className="function-selector">
      {!isOpen ? (
        <button onClick={() => setIsOpen(true)} className="btn-edit-functions">
          {selected.length > 0 ? `${selected.length} functions` : 'Assign Functions'}
        </button>
      ) : (
        <div className="function-selector-modal">
          {scope !== 'portal' && (
            <label className="function-checkbox authorize-all" style={{ marginBottom: '12px', padding: '8px', background: authorizedForAll ? '#d4edda' : '#e0f2fe', borderRadius: '6px', fontWeight: '600' }}>
              <input
                type="checkbox"
                checked={authorizedForAll}
                onChange={toggleAll}
              />
              <span>Authorize All Functions {authorizedForAll && '(includes future apps)'}</span>
            </label>
          )}
          {loadingFns ? (
            <p>Loading functions...</p>
          ) : (
            <div className="function-tabs-wrap">
              {/* Tabs rather than side-by-side columns: the SaaS list alone ran
                  to nineteen rows, which is what made this unreadable. A single
                  group needs no tab strip. */}
              {groupNames.length > 1 && (
                <div className="function-tabs" role="tablist">
                  {groupNames.map(name => {
                    const codes = (allFunctions[name] || []).map(f => f.code);
                    const chosen = codes.filter(c => selected.includes(c)).length;
                    return (
                      <button
                        key={name}
                        type="button"
                        role="tab"
                        aria-selected={activeGroup === name}
                        className={activeGroup === name ? 'function-tab active' : 'function-tab'}
                        onClick={() => setActiveGroup(name)}
                      >
                        {name}
                        {chosen > 0 && <span className="function-tab-count">{chosen}</span>}
                      </button>
                    );
                  })}
                </div>
              )}

              <div className="function-group">
                <label className="function-checkbox function-select-all">
                  <input
                    type="checkbox"
                    checked={activeAllSelected}
                    ref={el => { if (el) el.indeterminate = activeChosen > 0 && !activeAllSelected; }}
                    onChange={() => toggleGroup(activeCodes, activeAllSelected)}
                  />
                  <span>All {activeGroup} ({activeChosen}/{activeCodes.length})</span>
                </label>

                <div className="function-checkbox-list">
                  {(allFunctions[activeGroup] || []).map(fn => (
                    <label key={fn.code} className="function-checkbox">
                      <input
                        type="checkbox"
                        checked={selected.includes(fn.code)}
                        onChange={() => toggleFunction(fn.code)}
                      />
                      <span>{fn.code} - {fn.name}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          )}
          <div className="function-selector-actions">
            <button onClick={handleSave} className="btn-save">Save</button>
            <button onClick={handleCancel} className="btn-cancel">Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
};

function AuditLogs() {
  const { user } = useContext(AuthContext);
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') || 'logs';
  const [activeTab, setActiveTab] = useState(initialTab);
  const paramTab = searchParams.get('tab');
  const paramSurface = searchParams.get('surface');

  // Audit Logs state
  const [logs, setLogs] = useState([]);
  const [logsPage, setLogsPage] = useState(1);
  const [logsPagination, setLogsPagination] = useState({});
  const [logsFilters, setLogsFilters] = useState({
    action: '',
    resource: '',
    status: '',
    search: ''
  });

  // Active Users state
  const [activeUsers, setActiveUsers] = useState([]);

  // Stats state
  const [stats, setStats] = useState(null);

  // User management state
  const [allUsers, setAllUsers] = useState([]);
  const [userSurface, setUserSurface] = useState('employees'); // 'employees' | 'customers'

  /* Deep links have to work when this page is ALREADY open. The notification
   * bell navigates to /audit-logs?tab=users&surface=customers, but if the admin
   * is sitting on the audit log the route does not remount — React Router just
   * swaps the query string — so the useState seeds above never re-run and the
   * click appeared to do nothing. Syncing on the params themselves covers both
   * the fresh-load and already-mounted cases. */
  useEffect(() => {
    if (paramTab) setActiveTab(paramTab);
  }, [paramTab]);

  useEffect(() => {
    if (paramSurface === 'customers' || paramSurface === 'employees') setUserSurface(paramSurface);
  }, [paramSurface]);
  const [customerCompanyFilter, setCustomerCompanyFilter] = useState('');

  // Function definitions state
  const [functionDefs, setFunctionDefs] = useState([]);
  const [editingFn, setEditingFn] = useState(null);       // null = not editing, object = editing/creating
  const [fnLoading, setFnLoading] = useState(false);

  // Redmine user mapping state
  const [redmineUsers, setRedmineUsers] = useState([]);
  const [redmineSyncing, setRedmineSyncing] = useState(false);
  const [redmineLastSync, setRedmineLastSync] = useState(null);
  const [redmineSortField, setRedmineSortField] = useState('redmineId');
  const [redmineSortDir, setRedmineSortDir] = useState('asc');
  const [redmineSearch, setRedmineSearch] = useState('');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Initialize WebSocket
  useEffect(() => {
    initiateSocket();
    const socket = getSocket();

    if (socket) {
      // Listen for active users updates
      socket.on('users:active', (users) => {
        setActiveUsers(users);
      });

      // Listen for new audit logs
      socket.on('audit:new', (log) => {
        if (activeTab === 'logs') {
          setLogs(prev => [log, ...prev]);
        }
      });
    }

    return () => {
      if (socket) {
        socket.off('users:active');
        socket.off('audit:new');
      }
    };
  }, [activeTab]);

  // Fetch audit logs
  useEffect(() => {
    if (activeTab === 'logs') {
      fetchLogs();
    }
  }, [activeTab, logsPage, logsFilters]);

  // Fetch stats
  useEffect(() => {
    if (activeTab === 'stats') {
      fetchStats();
    }
  }, [activeTab]);

  // Fetch active users
  useEffect(() => {
    if (activeTab === 'active') {
      fetchActiveSessions();
    }
  }, [activeTab]);

  // Fetch all users
  useEffect(() => {
    if (activeTab === 'users') {
      fetchAllUsers();
    }
  }, [activeTab]);

  // Fetch function definitions
  useEffect(() => {
    if (activeTab === 'functions') {
      fetchFunctionDefs();
    }
  }, [activeTab]);

  // Fetch Redmine user mappings
  useEffect(() => {
    if (activeTab === 'redmine') {
      fetchRedmineUsers();
    }
  }, [activeTab]);

  const fetchLogs = async () => {
    try {
      setLoading(true);
      const params = {
        page: logsPage,
        limit: 50,
        ...logsFilters
      };

      const { data } = await axiosInstance.get('/audit/logs', { params });
      setLogs(data.logs);
      setLogsPagination(data.pagination);
      setError(null);
    } catch (err) {
      setError('Failed to load audit logs');
      console.error('Error fetching logs:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchActiveSessions = async () => {
    try {
      setLoading(true);
      const { data } = await axiosInstance.get('/audit/sessions/active');
      setActiveUsers(data.sessions);
      setError(null);
    } catch (err) {
      setError('Failed to load active sessions');
      console.error('Error fetching sessions:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      setLoading(true);
      const { data } = await axiosInstance.get('/audit/stats');
      setStats(data);
      setError(null);
    } catch (err) {
      setError('Failed to load statistics');
      console.error('Error fetching stats:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllUsers = async () => {
    try {
      setLoading(true);
      const { data } = await axiosInstance.get('/audit/users');
      console.log('[AuditLogs] Fetched users:', data.users.map(u => ({ _id: u._id, email: u.email })));
      setAllUsers(data.users);
      setError(null);
    } catch (err) {
      setError('Failed to load users');
      console.error('Error fetching users:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchFunctionDefs = async () => {
    try {
      setFnLoading(true);
      const { data } = await axiosInstance.get('/function-definitions');
      setFunctionDefs(data.functions || []);
    } catch (err) {
      console.error('Error fetching function definitions:', err);
    } finally {
      setFnLoading(false);
    }
  };

  const handleSaveFunctionDef = async (fnData) => {
    try {
      if (fnData._id) {
        // Update existing
        await axiosInstance.put(`/function-definitions/${fnData._id}`, fnData);
      } else {
        // Create new
        await axiosInstance.post('/function-definitions', fnData);
      }
      setEditingFn(null);
      fetchFunctionDefs();
    } catch (err) {
      alert('Failed to save: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleDeleteFunctionDef = async (id, code) => {
    if (!window.confirm(`Delete function "${code}"? This cannot be undone.`)) return;
    try {
      await axiosInstance.delete(`/function-definitions/${id}`);
      fetchFunctionDefs();
    } catch (err) {
      alert('Failed to delete: ' + (err.response?.data?.message || err.message));
    }
  };

  const fetchRedmineUsers = async () => {
    try {
      setLoading(true);
      const { data } = await axiosInstance.get('/redmine/users');
      setRedmineUsers(data.users || []);
      // Get the most recent lastSyncedAt
      if (data.users?.length > 0) {
        const mostRecent = data.users.reduce((latest, u) =>
          new Date(u.lastSyncedAt) > new Date(latest.lastSyncedAt) ? u : latest
        );
        setRedmineLastSync(mostRecent.lastSyncedAt);
      }
      setError(null);
    } catch (err) {
      setError('Failed to load Redmine users');
      console.error('Error fetching Redmine users:', err);
    } finally {
      setLoading(false);
    }
  };

  const syncRedmineUsers = async () => {
    try {
      setRedmineSyncing(true);
      const { data } = await axiosInstance.post('/redmine/users/sync');
      alert(`Sync complete: ${data.total} users (${data.created} new, ${data.updated} updated, ${data.deactivated} deactivated)`);
      fetchRedmineUsers();
    } catch (err) {
      alert('Failed to sync Redmine users: ' + (err.response?.data?.error || err.message));
    } finally {
      setRedmineSyncing(false);
    }
  };

  const handlePromoteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to promote this user to Super Admin?')) {
      return;
    }

    try {
      await axiosInstance.post(`/audit/users/${userId}/promote`);
      alert('User promoted to Super Admin successfully');
      fetchAllUsers();
    } catch (err) {
      alert('Failed to promote user: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleDemoteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to demote this user from Super Admin?')) {
      return;
    }

    try {
      await axiosInstance.post(`/audit/users/${userId}/demote`);
      alert('User demoted from Super Admin successfully');
      fetchAllUsers();
    } catch (err) {
      alert('Failed to demote user: ' + (err.response?.data?.message || err.message));
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds) return 'N/A';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    if (hours > 0) return `${hours}h ${minutes}m`;
    if (minutes > 0) return `${minutes}m ${secs}s`;
    return `${secs}s`;
  };

  const formatTimestamp = (timestamp) => {
    return new Date(timestamp).toLocaleString();
  };

  const getActionColor = (action) => {
    const colors = {
      'LOGIN': '#4CAF50',
      'LOGOUT': '#FF9800',
      'CREATE': '#2196F3',
      'UPDATE': '#FFC107',
      'DELETE': '#F44336',
      'READ': '#9E9E9E',
      'ACCESS_DENIED': '#E91E63',
      'ERROR': '#F44336'
    };
    return colors[action] || '#757575';
  };

  const getStatusBadge = (status) => {
    const colors = {
      'success': '#4CAF50',
      'failure': '#FF9800',
      'error': '#F44336'
    };
    return (
      <span style={{
        backgroundColor: colors[status] || '#757575',
        color: 'white',
        padding: '2px 8px',
        borderRadius: '4px',
        fontSize: '12px'
      }}>
        {status}
      </span>
    );
  };

  // Render Audit Logs Tab
  const renderLogsTab = () => (
    <div className="audit-logs-tab">
      <h2>Audit Logs</h2>

      {/* Filters */}
      <div className="filters-section">
        <input
          type="text"
          placeholder="Search..."
          value={logsFilters.search}
          onChange={(e) => setLogsFilters({ ...logsFilters, search: e.target.value })}
        />

        <select
          value={logsFilters.action}
          onChange={(e) => setLogsFilters({ ...logsFilters, action: e.target.value })}
        >
          <option value="">All Actions</option>
          <option value="LOGIN">Login</option>
          <option value="LOGOUT">Logout</option>
          <option value="CREATE">Create</option>
          <option value="UPDATE">Update</option>
          <option value="DELETE">Delete</option>
          <option value="READ">Read</option>
        </select>

        <select
          value={logsFilters.status}
          onChange={(e) => setLogsFilters({ ...logsFilters, status: e.target.value })}
        >
          <option value="">All Status</option>
          <option value="success">Success</option>
          <option value="failure">Failure</option>
          <option value="error">Error</option>
        </select>

        <button onClick={fetchLogs}>Refresh</button>
      </div>

      {/* Logs Table */}
      {loading ? (
        <p>Loading...</p>
      ) : (
        <>
          <div className="logs-table-container">
            <table className="logs-table">
              <thead>
                <tr>
                  <th>Timestamp</th>
                  <th>User</th>
                  <th>Action</th>
                  <th>Resource</th>
                  <th>Endpoint</th>
                  <th>Status</th>
                  <th>IP Address</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((log) => (
                  <tr key={log._id}>
                    <td>{formatTimestamp(log.timestamp)}</td>
                    <td>{log.username}</td>
                    <td>
                      <span style={{ color: getActionColor(log.action), fontWeight: 'bold' }}>
                        {log.action}
                      </span>
                    </td>
                    <td>{log.resource}</td>
                    <td className="endpoint-cell">{log.endpoint}</td>
                    <td>{getStatusBadge(log.status)}</td>
                    <td>{log.ipAddress}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="pagination">
            <button
              disabled={logsPage === 1}
              onClick={() => setLogsPage(logsPage - 1)}
            >
              Previous
            </button>
            <span>Page {logsPage} of {logsPagination.pages || 1}</span>
            <button
              disabled={logsPage >= (logsPagination.pages || 1)}
              onClick={() => setLogsPage(logsPage + 1)}
            >
              Next
            </button>
          </div>
        </>
      )}
    </div>
  );

  // Render Active Users Tab
  const renderActiveTab = () => (
    <div className="active-users-tab">
      <h2>Active Users ({activeUsers.length})</h2>
      <button onClick={fetchActiveSessions}>Refresh</button>

      {loading ? (
        <p>Loading...</p>
      ) : (
        <div className="active-users-table-container">
          <table className="active-users-table">
            <thead>
              <tr>
                <th>User</th>
                <th>Email</th>
                <th>Role</th>
                <th>Current Page</th>
                <th>Connected</th>
                <th>Session Duration</th>
                <th>Idle Time</th>
                <th>IP Address</th>
              </tr>
            </thead>
            <tbody>
              {activeUsers.map((session) => (
                <tr key={session._id}>
                  <td>
                    <span className="online-indicator">●</span> {session.username}
                  </td>
                  <td>{session.email}</td>
                  <td>{session.userId?.isSuperAdmin || session.isSuperAdmin ? 'Super Admin' : (session.userId?.role || session.role)}</td>
                  <td>
                    {/* The path alone cannot describe pages that hold many tools
                        behind one route, so the selected tool is shown beneath it. */}
                    <div>{session.currentPage || 'Unknown'}</div>
                    {session.currentActivity && (
                      <div className="session-activity">{session.currentActivity}</div>
                    )}
                  </td>
                  <td>{formatTimestamp(session.connectedAt)}</td>
                  <td>{formatDuration(session.sessionDuration)}</td>
                  <td>{formatDuration(session.idleTime)}</td>
                  <td>{session.ipAddress}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  // Render Statistics Tab
  const renderStatsTab = () => (
    <div className="stats-tab">
      <h2>Statistics</h2>
      <button onClick={fetchStats}>Refresh</button>

      {loading ? (
        <p>Loading...</p>
      ) : stats ? (
        <div className="stats-grid">
          <div className="stat-card">
            <h3>Total Logs</h3>
            <p className="stat-number">{stats.totalLogs}</p>
          </div>

          <div className="stat-card">
            <h3>Actions by Type</h3>
            <ul>
              {stats.actionCounts.map(item => (
                <li key={item._id}>
                  <span style={{ color: getActionColor(item._id) }}>{item._id}</span>: {item.count}
                </li>
              ))}
            </ul>
          </div>

          <div className="stat-card">
            <h3>Resources</h3>
            <ul>
              {stats.resourceCounts.map(item => (
                <li key={item._id}>{item._id}: {item.count}</li>
              ))}
            </ul>
          </div>

          <div className="stat-card">
            <h3>Status Distribution</h3>
            <ul>
              {stats.statusCounts.map(item => (
                <li key={item._id}>{item._id}: {item.count}</li>
              ))}
            </ul>
          </div>
        </div>
      ) : null}
    </div>
  );

  const handleRoleChange = async (userId, newRole) => {
    if (!user?.isSuperAdmin) {
      alert('Only Super Admins can change user roles');
      return;
    }

    try {
      await axiosInstance.post(`/audit/users/${userId}/role`, { role: newRole });
      alert('User role updated successfully');
      fetchAllUsers();
    } catch (err) {
      alert('Failed to update role: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleFunctionChange = async (userId, functions, authorizedForAll = false) => {
    if (!user?.isSuperAdmin) {
      alert('Only Super Admins can change function assignments');
      return;
    }

    try {
      await axiosInstance.post(`/audit/users/${userId}/functions`, {
        allowedFunctions: functions,
        authorizedForAll
      });
      alert('User functions updated successfully');
      fetchAllUsers();
    } catch (err) {
      alert('Failed to update functions: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleCustomerApproval = async (userId, approve) => {
    if (!user?.isSuperAdmin) {
      alert('Only Super Admins can approve customers');
      return;
    }
    try {
      await axiosInstance.post(`/audit/users/${userId}/approve-customer`, { approve });
      fetchAllUsers();
    } catch (err) {
      alert('Failed: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleCompanyAccessChange = async (userId, companyAccess) => {
    if (!user?.isSuperAdmin) {
      alert('Only Super Admins can change company access');
      return;
    }

    try {
      await axiosInstance.post(`/audit/users/${userId}/company-access`, { companyAccess });
      alert('User company access updated successfully');
      fetchAllUsers();
    } catch (err) {
      alert('Failed to update company access: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleDenyCompanyRequest = async (userId, companyCode) => {
    if (!user?.isSuperAdmin) {
      alert('Only Super Admins can deny company access requests');
      return;
    }

    if (!window.confirm(`Deny access request for ${companyCode}?`)) {
      return;
    }

    try {
      await axiosInstance.delete(`/audit/users/${userId}/company-request/${companyCode}`);
      alert('Company access request denied');
      fetchAllUsers();
    } catch (err) {
      alert('Failed to deny request: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
      return;
    }

    console.log('[AuditLogs] Deleting user with ObjectId:', userId);

    try {
      await axiosInstance.delete(`/audit/users/${userId}`);
      alert('User deleted successfully');
      fetchAllUsers();
    } catch (err) {
      alert('Failed to delete user: ' + (err.response?.data?.message || err.message));
    }
  };

  // Render Redmine User Mapping Tab
  const handleRedmineSort = (field) => {
    if (redmineSortField === field) {
      setRedmineSortDir(redmineSortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setRedmineSortField(field);
      setRedmineSortDir('asc');
    }
  };

  const getSortedFilteredRedmineUsers = () => {
    let filtered = redmineUsers;

    // Apply search filter
    if (redmineSearch.trim()) {
      const search = redmineSearch.toLowerCase();
      filtered = redmineUsers.filter(ru =>
        ru.redmineId?.toString().includes(search) ||
        ru.firstName?.toLowerCase().includes(search) ||
        ru.lastName?.toLowerCase().includes(search) ||
        ru.login?.toLowerCase().includes(search) ||
        ru.email?.toLowerCase().includes(search)
      );
    }

    // Apply sorting
    return [...filtered].sort((a, b) => {
      let aVal = a[redmineSortField];
      let bVal = b[redmineSortField];

      // Handle numeric vs string comparison
      if (redmineSortField === 'redmineId') {
        aVal = Number(aVal) || 0;
        bVal = Number(bVal) || 0;
      } else {
        aVal = (aVal || '').toString().toLowerCase();
        bVal = (bVal || '').toString().toLowerCase();
      }

      if (aVal < bVal) return redmineSortDir === 'asc' ? -1 : 1;
      if (aVal > bVal) return redmineSortDir === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const renderSortIcon = (field) => {
    if (redmineSortField !== field) return <span className="sort-icon">⇅</span>;
    return <span className="sort-icon active">{redmineSortDir === 'asc' ? '↑' : '↓'}</span>;
  };

  const renderRedmineTab = () => {
    const sortedUsers = getSortedFilteredRedmineUsers();

    return (
      <div className="redmine-users-tab">
        <div className="redmine-header">
          <h2>Redmine User Mapping</h2>
          <div className="redmine-actions">
            <span className="sync-info">
              {redmineLastSync && `Last sync: ${formatTimestamp(redmineLastSync)}`}
            </span>
            <button
              onClick={syncRedmineUsers}
              disabled={redmineSyncing}
              className="btn-sync"
            >
              {redmineSyncing ? 'Syncing...' : 'Sync from Redmine'}
            </button>
            <button onClick={fetchRedmineUsers}>Refresh</button>
          </div>
        </div>

        <p className="redmine-description">
          This mapping allows ticket replies to be posted under the actual user's name instead of the admin API account.
          Users are matched by email address.
        </p>

        {loading ? (
          <p>Loading...</p>
        ) : redmineUsers.length === 0 ? (
          <div className="no-data">
            <p>No Redmine users synced yet. Click "Sync from Redmine" to import users.</p>
          </div>
        ) : (
          <div className="redmine-table-container">
            <div className="redmine-search-bar">
              <input
                type="text"
                placeholder="Search by ID, name, login, or email..."
                value={redmineSearch}
                onChange={(e) => setRedmineSearch(e.target.value)}
                className="redmine-search-input"
              />
              {redmineSearch && (
                <button
                  className="clear-search"
                  onClick={() => setRedmineSearch('')}
                >
                  ✕
                </button>
              )}
            </div>
            <table className="redmine-table">
              <thead>
                <tr>
                  <th onClick={() => handleRedmineSort('redmineId')} className="sortable">
                    Redmine ID {renderSortIcon('redmineId')}
                  </th>
                  <th onClick={() => handleRedmineSort('firstName')} className="sortable">
                    First Name {renderSortIcon('firstName')}
                  </th>
                  <th onClick={() => handleRedmineSort('lastName')} className="sortable">
                    Last Name {renderSortIcon('lastName')}
                  </th>
                  <th onClick={() => handleRedmineSort('login')} className="sortable">
                    Login {renderSortIcon('login')}
                  </th>
                  <th onClick={() => handleRedmineSort('email')} className="sortable">
                    Email {renderSortIcon('email')}
                  </th>
                  <th onClick={() => handleRedmineSort('lastSyncedAt')} className="sortable">
                    Last Synced {renderSortIcon('lastSyncedAt')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {sortedUsers.map((ru) => (
                  <tr key={ru.redmineId}>
                    <td>{ru.redmineId}</td>
                    <td>{ru.firstName}</td>
                    <td>{ru.lastName}</td>
                    <td><strong>{ru.login}</strong></td>
                    <td>{ru.email}</td>
                    <td>{formatTimestamp(ru.lastSyncedAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="user-count">
              {sortedUsers.length === redmineUsers.length
                ? `${redmineUsers.length} users mapped`
                : `Showing ${sortedUsers.length} of ${redmineUsers.length} users`
              }
            </p>
          </div>
        )}
      </div>
    );
  };

  // Render Users Management Tab
  const renderUsersTab = () => (
    <div className="users-tab">
      <h2>User Management</h2>
      <button onClick={fetchAllUsers}>Refresh</button>

      {/* Styling lives in AuditLogs.css: universal.css overrides button box
          properties with !important, which inline styles cannot beat. */}
      <div className="user-surface-toggle" role="tablist" aria-label="User type">
        <button
          type="button"
          role="tab"
          aria-selected={userSurface === 'employees'}
          className={userSurface === 'employees' ? 'user-surface-tab active' : 'user-surface-tab'}
          onClick={() => setUserSurface('employees')}
        >Employees</button>
        <button
          type="button"
          role="tab"
          aria-selected={userSurface === 'customers'}
          className={userSurface === 'customers' ? 'user-surface-tab active' : 'user-surface-tab'}
          onClick={() => setUserSurface('customers')}
        >
          Customers
          {(() => {
            const pending = allUsers.filter(u => u.accountType === 'customer' && u.role === 'pending').length;
            return pending > 0
              ? <span className="user-surface-count" title={`${pending} awaiting approval`}>{pending}</span>
              : null;
          })()}
        </button>
      </div>

      {userSurface === 'customers' && (
        <div className="customers-view">
          <div style={{ marginBottom: 12 }}>
            <label style={{ fontSize: 13, fontWeight: 600, marginRight: 8 }}>Company:</label>
            <select value={customerCompanyFilter} onChange={(e) => setCustomerCompanyFilter(e.target.value)}>
              <option value="">All companies</option>
              {[...new Set(allUsers.filter(u => u.accountType === 'customer')
                  .flatMap(u => u.companyAccess || []))].sort().map(c => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>
          <table className="users-table">
            <thead>
              <tr>
                <th>Email</th><th>Name</th><th>Company</th><th>Allowed Apps</th><th>Status</th><th>Last Login</th><th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {allUsers
                .filter(u => u.accountType === 'customer')
                .filter(u => !customerCompanyFilter || (u.companyAccess || []).includes(customerCompanyFilter))
                .sort((a, b) => (a.role === 'pending' ? -1 : 1) - (b.role === 'pending' ? -1 : 1))
                .map(u => (
                  <tr key={u._id}>
                    <td>{u.email}</td>
                    <td>{[u.firstName, u.lastName].filter(Boolean).join(' ') || '—'}</td>
                    <td>{(u.companyAccess || []).join(', ') || <span style={{ color: '#b91c1c' }}>none</span>}</td>
                    <td>
                      {/* Per-customer app entitlement. Not every company gets the
                          same apps, so approval grants the baseline set and this
                          is where it is trimmed or extended. */}
                      <FunctionSelector
                        scope="portal"
                        userId={u._id}
                        currentFunctions={u.allowedFunctions || []}
                        authorizedForAll={false}
                        onChange={handleFunctionChange}
                      />
                    </td>
                    <td>{u.role === 'pending'
                          ? <span style={{ color: '#b45309', fontWeight: 600 }}>⚠ Pending</span>
                          : <span style={{ color: '#15803d', fontWeight: 600 }}>Approved</span>}</td>
                    <td>{u.lastLogin ? new Date(u.lastLogin).toLocaleString() : '—'}</td>
                    <td>
                      {u.role === 'pending'
                        ? <button type="button" onClick={() => handleCustomerApproval(u._id, true)}>Approve</button>
                        : <button type="button" onClick={() => handleCustomerApproval(u._id, false)}>Revoke</button>}
                    </td>
                  </tr>
                ))}
              {allUsers.filter(u => u.accountType === 'customer').length === 0 && (
                <tr><td colSpan="6" style={{ textAlign: 'center', padding: 24, color: '#64748b' }}>
                  No customer accounts yet. They appear here after their first portal sign-in.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {userSurface === 'employees' && (loading ? (
        <p>Loading...</p>
      ) : (
        <div className="users-table-container">
          <table className="users-table">
            <thead>
              <tr>
                <th>Status</th>
                <th>Email</th>
                <th>Name</th>
                <th>Role</th>
                <th>Allowed Functions</th>
                <th>Company Access</th>
                <th>Last Login</th>
                <th>Total Sessions</th>
                <th>Total Actions</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {allUsers.filter(u => (u.accountType || 'employee') !== 'customer').map((usr) => (
                <tr key={usr._id}>
                  <td>
                    {usr.isOnline ? (
                      <span className="online-indicator">● Online</span>
                    ) : (
                      <span className="offline-indicator">○ Offline</span>
                    )}
                  </td>
                  <td>{usr.email}</td>
                  <td>{usr.firstName} {usr.lastName}</td>
                  <td>
                    {user?.isSuperAdmin && usr._id !== user?.id ? (
                      <select
                        value={usr.isSuperAdmin ? 'super_admin' : usr.role}
                        onChange={(e) => handleRoleChange(usr._id, e.target.value)}
                        className="role-dropdown"
                      >
                        <option value="pending">Pending</option>
                        <option value="user">User</option>
                        <option value="sales">Sales</option>
                        <option value="admin">Admin</option>
                        <option value="super_admin">Super Admin</option>
                      </select>
                    ) : (
                      <span>{usr.isSuperAdmin ? 'Super Admin' : (usr.role || 'pending')}</span>
                    )}
                  </td>
                  <td>
                    {user?.isSuperAdmin && usr._id !== user?.id && !usr.isSuperAdmin ? (
                      <FunctionSelector
                        userId={usr._id}
                        currentFunctions={usr.allowedFunctions || []}
                        authorizedForAll={usr.authorizedForAll || false}
                        onChange={handleFunctionChange}
                      />
                    ) : usr.isSuperAdmin ? (
                      <span style={{color: '#28a745', fontWeight: 'bold'}}>All Access</span>
                    ) : usr.authorizedForAll ? (
                      <span style={{color: '#28a745', fontWeight: 'bold'}}>All Access</span>
                    ) : (
                      <span>{(usr.allowedFunctions || []).join(', ') || 'None'}</span>
                    )}
                  </td>
                  <td>
                    {user?.isSuperAdmin && usr._id !== user?.id && !usr.isSuperAdmin ? (
                      <CompanyAccessSelector
                        userId={usr._id}
                        currentCompanyAccess={{
                          companyAccess: usr.companyAccess || [],
                          pendingCompanyRequests: usr.pendingCompanyRequests || []
                        }}
                        onChange={handleCompanyAccessChange}
                        onDenyRequest={handleDenyCompanyRequest}
                      />
                    ) : usr.isSuperAdmin ? (
                      <span style={{color: '#28a745', fontWeight: 'bold'}}>All Companies</span>
                    ) : (usr.companyAccess || []).includes('*') ? (
                      <span style={{color: '#28a745', fontWeight: 'bold'}}>All Companies</span>
                    ) : (
                      <span>{(usr.companyAccess || []).join(', ') || 'None'}</span>
                    )}
                  </td>
                  <td>{formatTimestamp(usr.lastLogin)}</td>
                  <td>{usr.totalSessions}</td>
                  <td>{usr.totalActions}</td>
                  <td>
                    {usr._id !== user?.id && (
                      <button
                        className="btn-delete"
                        onClick={() => handleDeleteUser(usr._id)}
                      >
                        Delete
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );

  // Function Definitions tab — one Edit button per category group
  // Function users modal state
  const [fnUsersModal, setFnUsersModal] = useState(null); // { code, name } or null
  const [fnAllUsers, setFnAllUsers] = useState([]);        // all users from API
  const [fnUsersLoading, setFnUsersLoading] = useState(false);
  const [fnShowAll, setFnShowAll] = useState(false);       // toggle to show all users for adding
  const [fnSearch, setFnSearch] = useState('');

  const openFnUsersModal = async (code, name) => {
    setFnUsersModal({ code, name });
    setFnUsersLoading(true);
    setFnShowAll(false);
    setFnSearch('');
    try {
      const { data } = await axiosInstance.get('/audit/users');
      setFnAllUsers(data.users || []);
    } catch (err) {
      console.error('Failed to fetch users:', err);
      setFnAllUsers([]);
    } finally {
      setFnUsersLoading(false);
    }
  };

  const userHasFunction = (u, code) =>
    u.isSuperAdmin || u.authorizedForAll || (u.allowedFunctions || []).includes(code);

  const toggleUserFunction = async (userId, code, currentFunctions, hasIt) => {
    const updated = hasIt
      ? currentFunctions.filter(c => c !== code)
      : [...currentFunctions, code];
    try {
      await axiosInstance.post(`/audit/users/${userId}/functions`, {
        allowedFunctions: updated,
        authorizedForAll: false,
      });
      // Refresh the user list
      const { data } = await axiosInstance.get('/audit/users');
      setFnAllUsers(data.users || []);
    } catch (err) {
      alert('Failed to update: ' + (err.response?.data?.message || err.message));
    }
  };

  const [editingCategory, setEditingCategory] = useState(null); // 'SaaS' | 'On-Prem' | 'Admin' | null
  const [editDrafts, setEditDrafts] = useState([]);             // working copies while editing

  const startEditingCategory = (category, functions) => {
    setEditingCategory(category);
    setEditDrafts(functions.map(fn => ({ ...fn })));
  };

  const cancelEditingCategory = () => {
    setEditingCategory(null);
    setEditDrafts([]);
  };

  const updateDraft = (index, field, value) => {
    setEditDrafts(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const saveAllDrafts = async () => {
    try {
      for (const draft of editDrafts) {
        if (draft._id) {
          await axiosInstance.put(`/function-definitions/${draft._id}`, draft);
        } else {
          await axiosInstance.post('/function-definitions', draft);
        }
      }
      setEditingCategory(null);
      setEditDrafts([]);
      fetchFunctionDefs();
    } catch (err) {
      alert('Failed to save: ' + (err.response?.data?.message || err.message));
    }
  };

  const addDraftRow = (category) => {
    setEditDrafts(prev => [...prev, {
      code: '', name: '', category, route: '', description: '',
      minAccess: 'User', sortOrder: (prev.length + 1), enabled: true,
    }]);
  };

  const removeDraftRow = (index) => {
    const draft = editDrafts[index];
    if (draft._id) {
      if (!window.confirm(`Delete "${draft.code}"? This cannot be undone.`)) return;
      axiosInstance.delete(`/function-definitions/${draft._id}`)
        .then(() => setEditDrafts(prev => prev.filter((_, i) => i !== index)))
        .catch(err => alert('Failed to delete: ' + (err.response?.data?.message || err.message)));
    } else {
      setEditDrafts(prev => prev.filter((_, i) => i !== index));
    }
  };

  const renderFunctionsTab = () => {
    const saas = functionDefs.filter(f => f.category === 'SaaS');
    const onPrem = functionDefs.filter(f => f.category === 'On-Prem');
    const admin = functionDefs.filter(f => f.category === 'Admin');

    const inputStyle = { padding: '4px', background: '#333', color: '#fff', border: '1px solid #555', borderRadius: '4px' };

    const renderTable = (title, category, functions) => {
      const isEditing = editingCategory === category;

      return (
        <div style={{ marginBottom: '32px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
            <h3 style={{ margin: 0, color: '#e0e0e0' }}>{title}</h3>
            {!isEditing && !editingCategory && (
              <button
                onClick={() => startEditingCategory(category, functions)}
                className="btn-edit-functions"
                style={{ padding: '4px 14px', fontSize: '12px' }}
              >
                Edit
              </button>
            )}
            {isEditing && (
              <>
                <button onClick={saveAllDrafts} className="btn-save" style={{ padding: '4px 14px', fontSize: '12px' }}>Save All</button>
                <button onClick={cancelEditingCategory} className="btn-cancel" style={{ padding: '4px 14px', fontSize: '12px' }}>Cancel</button>
                <button onClick={() => addDraftRow(category)} style={{ padding: '4px 14px', fontSize: '12px' }}>+ Add Row</button>
              </>
            )}
          </div>
          <table className="users-table">
            <thead>
              <tr>
                <th style={{ width: '80px' }}>Code</th>
                <th>Name</th>
                <th>Route</th>
                <th>Description</th>
                <th style={{ width: '60px' }}>Order</th>
                <th style={{ width: '60px' }}>Active</th>
                {isEditing && <th style={{ width: '60px' }}></th>}
              </tr>
            </thead>
            <tbody>
              {isEditing ? (
                editDrafts.map((fn, idx) => (
                  <tr key={fn._id || `new-${idx}`}>
                    <td>
                      <input type="text" value={fn.code} disabled={!!fn._id}
                        onChange={e => updateDraft(idx, 'code', e.target.value)}
                        style={{ ...inputStyle, width: '70px', background: fn._id ? '#555' : '#333' }}
                        placeholder="SA12" />
                    </td>
                    <td>
                      <input type="text" value={fn.name}
                        onChange={e => updateDraft(idx, 'name', e.target.value)}
                        style={{ ...inputStyle, width: '100%' }} placeholder="Function Name" />
                    </td>
                    <td>
                      <input type="text" value={fn.route}
                        onChange={e => updateDraft(idx, 'route', e.target.value)}
                        style={{ ...inputStyle, width: '100%', fontFamily: 'monospace' }} placeholder="/route" />
                    </td>
                    <td>
                      <input type="text" value={fn.description || ''}
                        onChange={e => updateDraft(idx, 'description', e.target.value)}
                        style={{ ...inputStyle, width: '100%' }} placeholder="Optional" />
                    </td>
                    <td>
                      <input type="number" value={fn.sortOrder}
                        onChange={e => updateDraft(idx, 'sortOrder', parseInt(e.target.value) || 0)}
                        style={{ ...inputStyle, width: '50px' }} />
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <input type="checkbox" checked={fn.enabled !== false}
                        onChange={e => updateDraft(idx, 'enabled', e.target.checked)} />
                    </td>
                    <td>
                      <button onClick={() => removeDraftRow(idx)} className="btn-delete"
                        style={{ padding: '2px 8px', fontSize: '11px' }}>X</button>
                    </td>
                  </tr>
                ))
              ) : (
                functions.map(fn => (
                  <tr key={fn._id} style={{ opacity: fn.enabled ? 1 : 0.5 }}>
                    <td><strong>{fn.code}</strong></td>
                    <td>{fn.name}</td>
                    <td style={{ fontFamily: 'monospace', fontSize: '13px' }}>
                      {fn.route}
                      <span
                        onClick={() => openFnUsersModal(fn.code, fn.name)}
                        title="View authorized users"
                        className="fn-users-icon"
                      >👥</span>
                    </td>
                    <td style={{ color: '#aaa', fontSize: '13px' }}>{fn.description || '—'}</td>
                    <td style={{ textAlign: 'center' }}>{fn.sortOrder}</td>
                    <td style={{ textAlign: 'center' }}>{fn.enabled ? 'Yes' : 'No'}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      );
    };

    return (
      <div className="users-tab">
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '8px' }}>
          <h2 style={{ margin: 0 }}>Function Definitions</h2>
          <button onClick={fetchFunctionDefs} style={{ padding: '4px 12px' }}>Refresh</button>
        </div>
        <p style={{ color: '#aaa', marginBottom: '20px' }}>
          All functions must be explicitly assigned to users via the User Management tab. Only Super Admins bypass function checks.
        </p>

        {fnLoading ? (
          <p>Loading...</p>
        ) : (
          <div className="users-table-container">
            {saas.length > 0 && renderTable('SaaS Tools', 'SaaS', saas)}
            {onPrem.length > 0 && renderTable('On-Prem Tools', 'On-Prem', onPrem)}
            {admin.length > 0 && renderTable('Admin Tools', 'Admin', admin)}
          </div>
        )}

        {/* Authorized Users Modal */}
        {fnUsersModal && (() => {
          const code = fnUsersModal.code;
          const search = fnSearch.toLowerCase();
          const authorizedUsers = fnAllUsers.filter(u => userHasFunction(u, code));
          const unauthedUsers = fnAllUsers
            .filter(u => !userHasFunction(u, code) && u.role !== 'pending')
            .filter(u => !search ||
              (u.firstName || '').toLowerCase().includes(search) ||
              (u.lastName || '').toLowerCase().includes(search) ||
              (u.email || '').toLowerCase().includes(search)
            );

          return (
            <div className="fn-modal-overlay" onClick={() => setFnUsersModal(null)}>
              <div className="fn-modal" onClick={e => e.stopPropagation()}>
                <div className="fn-modal-header">
                  <div>
                    <span className="fn-modal-code">{fnUsersModal.code}</span>
                    <span className="fn-modal-title">{fnUsersModal.name}</span>
                  </div>
                  <button className="fn-modal-close" onClick={() => setFnUsersModal(null)}>✕</button>
                </div>
                {fnUsersLoading ? (
                  <div className="fn-modal-loading">Loading...</div>
                ) : (
                  <div className="fn-modal-list">
                    {authorizedUsers.length === 0 && !fnShowAll && (
                      <div className="fn-modal-empty">No users have access to this function.</div>
                    )}
                    {authorizedUsers.map(u => {
                      return (
                        <div key={u._id} className="fn-modal-user">
                          <div className="fn-modal-user-info">
                            <span className="fn-modal-user-name">{u.firstName} {u.lastName}</span>
                            <span className="fn-modal-user-email">{u.email}</span>
                          </div>
                          <div className="fn-modal-user-meta">
                            <span className={`fn-modal-role fn-modal-role--${u.isSuperAdmin ? 'super' : u.role}`}>
                              {u.isSuperAdmin ? 'Super Admin' : u.role}
                            </span>
                            {u.isSuperAdmin ? (
                              <span className="fn-modal-access fn-modal-access--super">Super Admin</span>
                            ) : (
                              <button
                                className="fn-modal-toggle fn-modal-toggle--remove"
                                onClick={() => toggleUserFunction(u._id, code, u.allowedFunctions || [], true)}
                                title="Remove access"
                              >Remove</button>
                            )}
                          </div>
                        </div>
                      );
                    })}

                    {/* Add users section */}
                    {fnShowAll && (
                      <>
                        <div className="fn-modal-divider">Add Users</div>
                        <div className="fn-modal-search">
                          <input
                            type="text"
                            placeholder="Search users..."
                            value={fnSearch}
                            onChange={e => setFnSearch(e.target.value)}
                            autoFocus
                          />
                        </div>
                        {unauthedUsers.length === 0 ? (
                          <div className="fn-modal-empty">
                            {search ? 'No matching users found.' : 'All users already have access.'}
                          </div>
                        ) : (
                          unauthedUsers.map(u => (
                            <div key={u._id} className="fn-modal-user">
                              <div className="fn-modal-user-info">
                                <span className="fn-modal-user-name">{u.firstName} {u.lastName}</span>
                                <span className="fn-modal-user-email">{u.email}</span>
                              </div>
                              <div className="fn-modal-user-meta">
                                <span className={`fn-modal-role fn-modal-role--${u.role}`}>
                                  {u.role}
                                </span>
                                <button
                                  className="fn-modal-toggle fn-modal-toggle--add"
                                  onClick={() => toggleUserFunction(u._id, code, u.allowedFunctions || [], false)}
                                >Add</button>
                              </div>
                            </div>
                          ))
                        )}
                      </>
                    )}
                  </div>
                )}
                <div className="fn-modal-footer">
                  <span>{authorizedUsers.length} user{authorizedUsers.length !== 1 ? 's' : ''} with access</span>
                  <button
                    className={`fn-modal-footer-btn ${fnShowAll ? 'active' : ''}`}
                    onClick={() => { setFnShowAll(!fnShowAll); setFnSearch(''); }}
                  >
                    {fnShowAll ? 'Done' : '+ Add Users'}
                  </button>
                </div>
              </div>
            </div>
          );
        })()}
      </div>
    );
  };

  // Check if user is super admin
  if (!user?.isSuperAdmin) {
    return (
      <div className="audit-logs-page">
        <div className="access-denied">
          <h2>Access Denied</h2>
          <p>This page is only accessible to Super Administrators.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="audit-logs-page">
      <h1>Audit & Monitoring Dashboard</h1>

      {error && <div className="error-message">{error}</div>}

      {/* Tabs */}
      <div className="tabs">
        <button
          className={activeTab === 'logs' ? 'tab active' : 'tab'}
          onClick={() => setActiveTab('logs')}
        >
          Audit Logs
        </button>
        <button
          className={activeTab === 'active' ? 'tab active' : 'tab'}
          onClick={() => setActiveTab('active')}
        >
          Active Users ({activeUsers.length})
        </button>
        <button
          className={activeTab === 'stats' ? 'tab active' : 'tab'}
          onClick={() => setActiveTab('stats')}
        >
          Statistics
        </button>
        <button
          className={activeTab === 'users' ? 'tab active' : 'tab'}
          onClick={() => setActiveTab('users')}
        >
          User Management
        </button>
        <button
          className={activeTab === 'redmine' ? 'tab active' : 'tab'}
          onClick={() => setActiveTab('redmine')}
        >
          Redmine Mapping
        </button>
        <button
          className={activeTab === 'functions' ? 'tab active' : 'tab'}
          onClick={() => setActiveTab('functions')}
        >
          Function Definitions
        </button>
        {/* Door Functions tab - only visible to specific super admins */}
        {DOOR_FUNCTIONS_EMAILS.map(e => e.toLowerCase()).includes(user?.email?.toLowerCase()) && (
          <button
            className={activeTab === 'door' ? 'tab active' : 'tab'}
            onClick={() => setActiveTab('door')}
          >
            Door Functions
          </button>
        )}
      </div>

      {/* Tab Content */}
      <div className="tab-content">
        {activeTab === 'logs' && renderLogsTab()}
        {activeTab === 'active' && renderActiveTab()}
        {activeTab === 'stats' && renderStatsTab()}
        {activeTab === 'users' && renderUsersTab()}
        {activeTab === 'redmine' && renderRedmineTab()}
        {activeTab === 'functions' && renderFunctionsTab()}
        {activeTab === 'door' && <DoorFunctions />}
      </div>
    </div>
  );
}

export default AuditLogs;
